import React from 'react'
import axios from 'axios'
import { toastr } from '@/utils/toast'
import moment from 'moment'
import _ from 'lodash'

import config from '@/config/config-esm'
import { setModal, unsetModal } from '@/store/slices/modalSlice'
import { setFormData } from '@/store/slices/formDataSlice'

interface PersonLookupContext {
  dispatch: any
  formData: any
  onPersonUpdate: (person: any) => void
  capitalizeFirstLetter?: (str: string) => string
}

export default function lookupPerson(
  type: string,
  data: any,
  mode: string,
  fakeError: boolean,
  context: PersonLookupContext,
) {
  if (type === 'dsf') {
    return getDSFPerson(data, mode, fakeError, context)
  } else {
    return getDMPerson(data, mode, context)
  }
}

function getDSFPerson(data: any, mode: string, fakeError: boolean, context: PersonLookupContext) {
  const { ssn, firstName, lastName } = data
  const { dispatch, onPersonUpdate } = context

  const submitData = {
    ssn: ssn,
    firstname: firstName,
    lastname: lastName,
    foo: 'barX13+1',
  }

  if (
    !ssn ||
    ssn.length < 10 ||
    !firstName ||
    firstName.length < 1 ||
    !lastName ||
    lastName.length < 1
  ) {
    dispatch(
      setModal({
        show: true,
        heading: (
          <h1>
            Både personnummer, fornavn og etternavn må oppgis for å kunne utføre kontroll mot
            folkeregisteret.
          </h1>
        ),
        body: <p>Vennligst fyll ut dette.</p>,
        button: (
          <button
            className="secondary large"
            onClick={() => {
              dispatch(unsetModal())
            }}
          >
            Ok
          </button>
        ),
      }),
    )
  } else {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    if (fakeError) {
      setTimeout(() => {
        source.cancel('Operation canceled by the user.')
      }, 2000)
    }

    axios
      .post(`${config.apiUrl}/get_dsf_person`, submitData, { cancelToken: source.token })
      .then((r) => {
        console.log('responseData', r.data.data)
        const responseData = r.data && r.data.data ? r.data.data : null
        const address =
          typeof responseData.address === 'string'
            ? {
                street: responseData.address,
                zipCode: responseData.postalCode,
                city: responseData.postalArea,
              }
            : responseData.address || {}
        const hasAllRequired =
          responseData.firstName &&
          responseData.lastName &&
          address.street &&
          address.zipCode &&
          address.city
        const person = {
          verified: hasAllRequired,
          partlyVerified: !hasAllRequired,
          firstName:
            responseData.firstName + (responseData.middleName ? ` ${responseData.middleName}` : ''),
          lastName: responseData.lastName,
          email: data.email,
          ssn: submitData.ssn,
          address: address.street,
          postalCode: address.zipCode,
          postalArea: address.city,
          dateOfBirth: moment.utc(responseData.dateOfBirth, 'YYYY-MM-DD').toISOString(),
        }
        console.log('person', person)

        // Call the callback instead of setState
        onPersonUpdate({ ...data, ...person })
      })
      .catch((e) => {
        console.log('e', e)
        if (axios.isCancel(e)) {
          console.log('Request canceled', e.message)
          toastr.error(
            'Tidsavbrudd',
            `Kunne ikke slå opp personen i folkeregisteret innen rimelig tid.`,
          )
          const person = {
            verified: false,
            unableToVerify: true,
            firstName,
            lastName,
            ssn,
            dateOfBirth: moment.utc(ssn, 'YYYYMMDD').toISOString(),
          }
          console.log('person', person)
          // Mark as deferred in formData for deferred sale handling
          if (dispatch) {
            dispatch(setFormData({ personLookupDeferred: true }))
          }

          // Call the callback instead of setState
          onPersonUpdate({ ...data, ...person })
        } else {
          if (
            e &&
            e.response &&
            e.response.data &&
            e.response.data.errorCode === 'personNotFound'
          ) {
            // Mark as deferred in formData for deferred sale handling
            // if (dispatch) {
            //   dispatch(setFormData({ personLookupDeferred: true }))
            // }
            dispatch(
              setModal({
                show: true,
                heading: <h1>Personen ble ikke funnet i folkeregisteret.</h1>,
                body: (
                  <div>
                    <p>Det betyr at vi ikke kan registrere abonnement på vedkommende.</p>
                    <p>Sjekk at personnummer, fornavn og etternavn er riktig.</p>
                    <p>Ta kontakt med kundeservice på tlf 915 09 915 for mer informasjon.</p>
                    <br />
                  </div>
                ),
                button: (
                  <button
                    className="secondary large"
                    onClick={() => {
                      dispatch(unsetModal())
                    }}
                  >
                    Lukk
                  </button>
                ),
              }),
            )
          } else {
            toastr.error(
              'Folkeregisteret svarte ikke innen rimelig tid eller returnerte en feil',
              `Det betyr at vi ikke kan registrere abonnement på vedkommende akkurat nå.`,
            )
            const person = {
              verified: false,
              unableToVerify: true,
              firstName,
              lastName,
              ssn,
              dateOfBirth: moment.utc(ssn, 'YYYYMMDD').toISOString(),
            }
            console.log('person', person)
            // Mark as deferred in formData for deferred sale handling
            // if (dispatch) {
            //   dispatch(setFormData({ personLookupDeferred: true }))
            // }

            // Call the callback instead of setState
            onPersonUpdate({ ...data, ...person })
          }
        }
      })
  }
}

function getDMPerson(data: any, mode: string, context: PersonLookupContext) {
  const { formData, dispatch, onPersonUpdate, capitalizeFirstLetter } = context
  const { ssn, email, phone } = data

  // Default capitalizeFirstLetter function if not provided
  const capitalize =
    capitalizeFirstLetter ||
    ((str: string) => {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    })

  const submitData = {
    ssn: ssn,
    phone: formData.currentPhone,
  }

  if (!ssn || ssn.length < 10 || !phone || phone.length < 6) {
    dispatch(
      setModal({
        show: true,
        heading: <h1>Både telefonnr og personnr må oppgis for å kunne utføre kredittsjekk.</h1>,
        body: <p>Vennligst fyll ut dette.</p>,
        button: (
          <button
            className="secondary large"
            onClick={() => {
              dispatch(unsetModal())
            }}
          >
            Ok
          </button>
        ),
      }),
    )
  } else {
    if (email && email.length > 0) {
      submitData.email = email
    }

    axios
      .post(`${config.apiUrl}/get_dm_person`, submitData)
      .then((r) => {
        if (r.data.data) {
          const responseData = r.data.data
          console.log(responseData)
          // set date of birth on data from get_persons
          let dateOfBirth = null
          if (_.has(responseData.data[0], 'appInfo.memberValues.item')) {
            const dob = _.find(
              responseData.data[0].appInfo.memberValues.item,
              (i) => i.name === 'birthDate',
            )
            const dobFormat = _.find(
              responseData.data[0].appInfo.memberValues.item,
              (i) => i.name === 'dateFormat',
            )
            console.log(dob, dobFormat)
            dateOfBirth = moment.utc(dob.val, dobFormat.val.toUpperCase()).toISOString()
            console.log('dateOfBirth', dateOfBirth)
          }

          const person = {
            ssn: data.ssn,
          }

          if (dateOfBirth) {
            person.dateOfBirth = dateOfBirth
          }

          if (responseData.address) {
            person.address = responseData.address.street
              ? capitalize(responseData.address.street)
              : ''
            person.postalCode = responseData.address.zipCode
              ? capitalize(responseData.address.zipCode)
              : ''
            person.postalArea = responseData.address.city
              ? capitalize(responseData.address.city)
              : ''
          }
          if (responseData.firstName) {
            person.firstName = capitalize(responseData.firstName)
            if (responseData.middleName) {
              person.firstName += ' ' + capitalize(responseData.middleName)
            }
          }
          if (responseData.lastName) {
            person.lastName = capitalize(responseData.lastName)
          }

          const hasAllRequired =
            person.firstName &&
            person.lastName &&
            person.address &&
            person.postalCode &&
            person.postalArea
          person.verified = hasAllRequired
          person.partlyVerified = !hasAllRequired

          // Call the callback instead of setState
          onPersonUpdate({ ...data, ...person })
        }
      })
      .catch((e) => {
        toastr.error('Kunne ikke finne person', `Prøv på nytt eller kontakt teamleader`)
      })
  }
}

// For backward compatibility with the old binding pattern
export function createBoundLookupPerson(context: PersonLookupContext) {
  return (type: string, data: any, mode: string, fakeError?: boolean) => {
    return lookupPerson(type, data, mode, fakeError || false, context)
  }
}
